import React, { useState, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bug, X, Send, Loader2, RefreshCw, CheckCircle, AlertTriangle } from 'lucide-react';
import { db } from '../../core/firebase';
import { collection, addDoc } from 'firebase/firestore';
import AppContext from '../../core/context/AppContext';

export default function BetaFeedbackModal({ crashTrace = null, forceOpen = false }) {
  // Use React Context safely so it survives even if wrapped outside of a live provider scope during a crash
  const context = useContext(AppContext);
  const authUser = context?.authUser;
  const currentDifficultyTier = context?.currentDifficultyTier;
  const setToastMessage = context?.setToastMessage;

  const [isOpen, setIsOpen] = useState(forceOpen);
  const [feedbackText, setFeedbackText] = useState(crashTrace ? 'Automated Crash Report' : '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // Hide the floating button if unauthenticated, unless invoked by the ErrorBoundary
  if (!authUser && !crashTrace) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!feedbackText.trim() && !crashTrace) return;

    setIsSubmitting(true);
    try {
      const feedbackPayload = {
        feedbackText: feedbackText.trim(),
        uid: authUser?.uid || 'unauthenticated',
        userEmail: authUser?.email || 'unauthenticated',
        currentScreen: window.location.pathname,
        currentDifficultyTier: currentDifficultyTier || (crashTrace ? 'fatal-crash' : 'unknown'),
        timestamp: Date.now(),
        stackTrace: crashTrace || null // Silently appended for dev audit
      };

      await addDoc(collection(db, 'beta_feedback'), feedbackPayload);
      
      if (crashTrace) {
        setIsSuccess(true);
      } else {
        if (setToastMessage) setToastMessage("Telemetry Logged. Thank you.");
        setIsOpen(false);
        setFeedbackText('');
      }
    } catch (error) {
      console.error("Feedback submission failed:", error);
      if (setToastMessage) setToastMessage("Failed to submit feedback.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // If invoked inline by the Error Boundary, hide the floating trigger button
  if (forceOpen && !isOpen && !isSuccess) return null;

  return (
    <>
      {!forceOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed z-[100] bottom-20 left-4 sm:bottom-6 sm:left-6 bg-slate-800 text-white p-3 rounded-full shadow-2xl hover:bg-slate-700 transition-transform hover:scale-110 border border-slate-600 group"
          title="Report System Anomaly"
        >
          <Bug size={20} className="group-hover:text-blue-400 transition-colors" />
        </button>
      )}

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className={`fixed inset-0 z-[110] ${forceOpen ? 'bg-transparent relative' : 'bg-slate-900/60 backdrop-blur-sm'} flex items-center justify-center p-4`}
            onClick={() => !forceOpen && setIsOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.95, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 20 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-lg border border-slate-200 overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {isSuccess ? (
                 <div className="p-8 text-center flex flex-col items-center">
                    <CheckCircle size={64} className="text-emerald-500 mb-4" />
                    <h3 className="text-2xl font-black text-slate-800 mb-2">Crash Report Secured</h3>
                    <p className="text-slate-500 text-sm mb-8 font-medium">The engineering team has received the encrypted stack trace.</p>
                    <button 
                       onClick={() => window.location.reload()}
                       className="w-full py-4 bg-slate-900 hover:bg-black text-white font-black uppercase tracking-widest rounded-xl shadow-lg transition-transform active:scale-95 flex items-center justify-center gap-2"
                    >
                       <RefreshCw size={18} /> Reboot Node
                    </button>
                 </div>
              ) : (
                 <>
                    <div className="bg-slate-900 p-4 flex justify-between items-center">
                        <div className="flex items-center gap-2 text-white">
                        {crashTrace ? <AlertTriangle size={18} className="text-red-400" /> : <Bug size={18} className="text-blue-400" />}
                        <h3 className="font-bold text-sm tracking-widest uppercase">{crashTrace ? 'Fatal Crash Intercept' : 'Beta Feedback Logger'}</h3>
                        </div>
                        {!forceOpen && (
                        <button onClick={() => setIsOpen(false)} className="text-slate-400 hover:text-white transition-colors">
                            <X size={20} />
                        </button>
                        )}
                    </div>

                    <form onSubmit={handleSubmit} className="p-6 text-left">
                        <div className="mb-4 bg-slate-50 p-3 rounded-lg border border-slate-100 text-xs font-mono text-slate-500 space-y-1">
                        <p><span className="font-bold text-slate-700">Context Capture:</span></p>
                        <p>Screen: {window.location.pathname}</p>
                        <p>Tier: {currentDifficultyTier || (crashTrace ? 'fatal-crash' : 'unknown')}</p>
                        </div>

                        <label className="block text-sm font-bold text-slate-700 mb-2">
                            {crashTrace ? "What were you doing when the system crashed?" : "Anomaly Description"}
                        </label>
                        <textarea
                        value={feedbackText}
                        onChange={(e) => setFeedbackText(e.target.value)}
                        placeholder="Describe the bug or feedback here..."
                        className="w-full h-32 p-3 bg-white border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none resize-none text-sm transition-shadow"
                        disabled={isSubmitting}
                        required
                        />

                        <div className="mt-6 flex justify-end gap-3">
                        {!forceOpen && (
                            <button
                            type="button"
                            onClick={() => setIsOpen(false)}
                            className="px-5 py-2.5 text-sm font-bold text-slate-600 hover:bg-slate-100 rounded-xl transition-colors"
                            disabled={isSubmitting}
                            >
                            Cancel
                            </button>
                        )}
                        <button
                            type="submit"
                            disabled={isSubmitting || (!feedbackText.trim() && !crashTrace)}
                            className={`px-6 py-2.5 ${crashTrace ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'} w-full sm:w-auto text-white text-sm font-bold rounded-xl shadow-md transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:active:scale-100 active:scale-95`}
                        >
                            {isSubmitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                            {isSubmitting ? 'Logging...' : (crashTrace ? 'Submit Crash & Reboot' : 'Submit Report')}
                        </button>
                        </div>
                    </form>
                 </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}